import { GeoAutocompleteAddress } from '@brenger/api-client';
import { getLocation, push } from 'connected-react-router';
import React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { useDispatch } from 'react-redux';
import { Button, Dialog, IconSmile, Modal } from '../../../../brenger-shared-ui';
import van from '../../../../brenger-shared-ui/assets/images/van@2x.png';
import { clarity } from '../../../../configs/clarity';
import { useTypedSelector } from '../../../../hooks';
import { RootState, StopType } from '../../../../types';
import { priceAsString } from '../../../../utils/price';
import {
  actions,
  getInitialDatePath,
  getIsDateSelectionNotAvailable,
  getIsFlexDates,
  getQuote,
  getSelectedDateTime,
  getStopDestinationDetails,
  isRequestAboveMaxDistance,
} from '../../ducks';
import { DateStepParam } from '../SubStepDate';
import { getIsExcludingVat } from '../../../User/ducks';
import { setSentryContext } from '../../../../utils/sentry';
import * as Sentry from '@sentry/react';

export const LocalityChangeModal: React.FC<{ stopType: StopType }> = ({ stopType }) => {
  const dispatch = useDispatch();

  const newAddress = useTypedSelector((state: RootState) => state.generalTransport.contact[stopType]?.address);
  const localityWidget = useTypedSelector(state => getStopDestinationDetails(state, stopType)?.locality);

  const quote = useTypedSelector(getQuote);
  const localityChange = useTypedSelector((state: RootState) => state.generalTransport.localityChange);
  const priceLoading = useTypedSelector((state: RootState) => state.generalTransport.price.isLoading);
  const dateSelectionNotAvailable = useTypedSelector(getIsDateSelectionNotAvailable);
  const isAboveMaxDistance = useTypedSelector((state: RootState) => isRequestAboveMaxDistance(state));
  const isAboveMaxDateDistance = useTypedSelector((state: RootState) => isRequestAboveMaxDistance(state, true));
  const isFlexDates = useTypedSelector(getIsFlexDates);
  const isDateDistanceChanged =
    !dateSelectionNotAvailable &&
    (isAboveMaxDistance !== localityChange?.above_max_distance_old ||
      isAboveMaxDateDistance !== localityChange?.above_max_date_distance_old);
  const switchedToTrWithDates = !dateSelectionNotAvailable && localityChange?.date_selection_not_available_old;
  const switchtedToFlexDates =
    isFlexDates && typeof localityChange?.is_flex_dates_old === 'boolean' && !localityChange.is_flex_dates_old;
  const selectedDates = useTypedSelector((state: RootState) =>
    getSelectedDateTime(state, localityChange?.stopType || StopType.PICKUP)
  );
  const stayedFlexDates = isFlexDates && localityChange?.is_flex_dates_old;
  const dateSelectionRequired =
    !stayedFlexDates &&
    !switchtedToFlexDates &&
    ((switchedToTrWithDates && !selectedDates.length) || (!switchedToTrWithDates && isDateDistanceChanged));

  const datePath = useTypedSelector(getInitialDatePath);
  const { pathname } = useTypedSelector(getLocation);
  const isExclVat = useTypedSelector(getIsExcludingVat);
  const oldPrice = localityChange?.quote_old?.price[isExclVat ? 'excl_vat' : 'incl_vat'].amount || 0;
  const newPrice = useTypedSelector(
    (state: RootState) => state.generalTransport.quote?.price[isExclVat ? 'excl_vat' : 'incl_vat'].amount || 0
  );
  const priceTrans = oldPrice === newPrice ? 'equal' : oldPrice > newPrice ? 'lower' : 'higher';

  React.useEffect(() => {
    Sentry.setTag('locality_change', !!localityChange);
    if (!localityChange) {
      return;
    }
    const localityChangeData = {
      locality_old: localityChange?.locality_old,
      locality_new: localityChange?.locality_new,
      above_max_distance_old: localityChange?.above_max_distance_old,
      above_max_date_distance_old: localityChange?.above_max_date_distance_old,
      date_selection_not_available_old: localityChange?.date_selection_not_available_old,
      is_flex_dates_old: localityChange?.is_flex_dates_old,
      dateSelectionRequired,
      isFlexDates,
      stayedFlexDates,
      isDateDistanceChanged,
      switchtedToFlexDates: !!switchtedToFlexDates,
      switchedToTrWithDates: !!switchedToTrWithDates,
    };
    setSentryContext('locality_change', localityChangeData);
    Sentry.startInactiveSpan({
      name: 'locality_change',
      op: 'function',
      attributes: localityChangeData,
    });
    if (isDateDistanceChanged) {
      clarity.setTag('locality_change_date_distance');
      return;
    }
    clarity.setTag('locality_change');
  }, [localityChange]);

  React.useEffect(() => {
    // Both should be present
    if (!newAddress || !localityWidget || !newAddress.locality) return;

    if (newAddress.locality === localityWidget) return;
    // Set the locality change
    // FIXME: This is misuse of global state there isn't really a need of making other code aware
    // Should be refactored to local state, so remove redux actions etc.
    dispatch(
      actions.setLocalityChange({
        stopType: stopType,
        locality_old: localityWidget,
        locality_new: newAddress.locality,
        quote_old: quote,
        above_max_distance_old: isAboveMaxDistance,
        above_max_date_distance_old: isAboveMaxDateDistance,
        date_selection_not_available_old: dateSelectionNotAvailable,
        is_flex_dates_old: isFlexDates,
      })
    );
    // Blur any active input to prevent keyboard from staying open on mobile
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement) activeElement.blur();
    // Update the widget details
    const { lat, lng, ...rest } = newAddress;
    dispatch(
      actions.setDestinationDetails({ ...rest, latitude: lat, longitude: lng } as GeoAutocompleteAddress, stopType)
    );
  }, [newAddress, localityWidget]);

  /**
   * When to show this modal?
   * - LocalityChange should not be null and
   * - To be safe: We only want to show pickup changes on the pickup page and vice versa
   */
  if (!localityChange || !pathname.includes(stopType.toLowerCase())) {
    return null;
  }

  const transVars = {
    locality_old: localityChange?.locality_old,
    locality_new: localityChange?.locality_new,
    price_old: (
      <b className={'old-price text-primary'}>
        {priceAsString({ price: { amount: oldPrice }, addPlusMinusIndicator: false })}
      </b>
    ),
    price_new: (
      <b className={'text-primary'}>{priceAsString({ price: { amount: newPrice }, addPlusMinusIndicator: false })}</b>
    ),
    price_diff: (
      <b className={'text-primary'}>
        {priceAsString({ price: { amount: newPrice - oldPrice }, addPlusMinusIndicator: false })}
      </b>
    ),
  };

  const continueAction = (e): void => {
    e.preventDefault();
    /* Continue action shouldn't do anything, actions depend on pricing state */
    if (priceLoading) {
      return;
    }
    if (switchtedToFlexDates) {
      dispatch(actions.setShowFlexDatesModal(true));
    } else if (dateSelectionRequired) {
      /* When switched to national or date requirements are influenced, then we send the user back to dates */
      dispatch(push(`${datePath}?${DateStepParam.DONT_RESET}`));
    }
    /* In all cases we set the change to null, to hide the modal (and when dates can stay the same, the user can continue on the contact page) */
    dispatch(actions.setLocalityChange(null));
  };

  return (
    <Dialog isActive={true}>
      <Modal open={true} onClick={continueAction} style={'white'} size={'small'}>
        <div className={'font-bold text-primary text-center'}>
          <Translate id={'request_flow.locality_change.modal.title_' + localityChange?.stopType.toLowerCase()} />
        </div>
        <div className={'text-center pb-[1em] pt-[1em]'}>
          <img className={'van-assets-image inline-block'} src={van} />
        </div>
        <div className={'pb-[1em]'}>
          <Translate
            id={'request_flow.locality_change.modal.text_' + localityChange?.stopType.toLowerCase()}
            data={transVars}
          />
        </div>
        {priceLoading && (
          <div className={'pb-[1em]'}>
            <IconSmile spinning={true} /> <Translate id={'request_flow.locality_change.modal.loading_price'} />
          </div>
        )}
        {!priceLoading && (
          <div className={'pb-[1em]'}>
            <Translate id={`request_flow.locality_change.modal.text_price_${priceTrans}`} data={transVars} />
          </div>
        )}
        {!switchedToTrWithDates && isDateDistanceChanged && (
          <div className={'pb-[1em]'}>
            <Translate id={`request_flow.locality_change.modal.text_date`} />
          </div>
        )}
        {switchedToTrWithDates && dateSelectionRequired && (
          <div className={'pb-[1em]'}>
            <Translate id={`request_flow.locality_change.modal.text_date_switch_to_national`} />
          </div>
        )}
        {switchedToTrWithDates && !dateSelectionRequired && (
          <div className={'pb-[1em]'}>
            <Translate id={`request_flow.locality_change.modal.text_date_switch_to_national_dates_present`} />
          </div>
        )}
        <Button disabled={priceLoading} type={'button'} buttonStyle={'secondary'} onClick={continueAction}>
          <Translate id={'request_flow.locality_change.modal.cta' + (dateSelectionRequired ? '_dates' : '')} />
        </Button>
        {/* Offer a way to just continue when no action is needed */}
        {dateSelectionRequired && !!selectedDates.length && (
          <Button
            disabled={priceLoading}
            type={'button'}
            buttonStyle={'primary'}
            onClick={() => dispatch(actions.setLocalityChange(null))}
          >
            <Translate id={'request_flow.locality_change.modal.cta'} />
          </Button>
        )}
      </Modal>
    </Dialog>
  );
};
